import { onMounted, onUnmounted } from "vue";
import { useNuxtApp } from "#app";

export const useUploadSync = () => {
  const { $api, $indexdb } = useNuxtApp();
  let syncInterval: ReturnType<typeof setInterval> | null = null;
  let isSyncing = false;

  const syncPendingUploads = async () => {
    if (isSyncing || !navigator.onLine || !$indexdb) return;
    isSyncing = true;

    try {
      const sessions = await $indexdb.upload.getAllSessions();

      for (let session of sessions) {
        if (!session.isStartedOnServer) {
          try {
            const { uploadId: realId, key: realKey } =
              await $api.lead.startOHTUpload(session.lead_id, session.mimeType);

            await $indexdb.upload.setRealSessionData(
              session.uploadId,
              realId,
              realKey,
            );

            session.realUploadId = realId;
            session.realKey = realKey;
            session.isStartedOnServer = true;
          } catch (e) {
            console.warn(
              `[Sync] Backend inacessível para o Start. Pulando sessão.`,
            );
            continue;
          }
        }

        const targetUploadId = session.realUploadId || session.uploadId;
        const targetKey = session.realKey || session.key;

        const pendingChunks = await $indexdb.upload.getPendingChunks(
          session.uploadId,
        );

        for (const chunk of pendingChunks) {
          try {
            const url =
              chunk.retryUrl ||
              (
                await $api.lead.getOHTPartUrl({
                  uploadId: targetUploadId,
                  key: targetKey,
                  partNumber: chunk.partNumber,
                })
              ).url;
            const tag = await $api.storage.uploadPart(url, chunk.blob);
            await $indexdb.upload.markChunkUploaded(chunk.id, tag);
          } catch (e) {
            console.warn(
              `[Sync] Falha ao sincronizar o chunk ${chunk.id}. Interrompendo o sync para esta sessão.`,
              e,
            );
            break;
          }
        }

        if (session.isRecordingFinished) {
          const remainingChunks = await $indexdb.upload.getPendingChunks(
            session.uploadId,
          );
          if (remainingChunks.length === 0) {
            const allChunks = await $indexdb.upload.getAllChunks(
              session.uploadId,
            );
            allChunks.sort((a, b) => a.partNumber - b.partNumber);
            const parts = allChunks.map((c) => ({
              partNumber: c.partNumber,
              tag: c.tag!,
            }));

            try {
              await $api.lead.completeOHTUpload({
                uploadId: targetUploadId,
                key: targetKey,
                parts,
                duration: session.duration,
              });
              await $indexdb.upload.clearSessionData(session.uploadId);
            } catch (e) {
              console.error(`[Sync] Erro ao dar complete`);

              const isRecoverable = await handleCompleteError(
                e,
                session.uploadId,
                $indexdb,
              );
              if (isRecoverable) {
                console.log(
                  "[Sync] Partes corrompidas identificadas. Reenvio iniciando.",
                );
                setTimeout(() => syncPendingUploads(), 0);
              }
            }
          }
        }
      }
    } catch (error: any) {
      if (error?.name === "InvalidStateError") {
        console.warn(
          "[Sync] Conexão IndexedDB fechada, será reaberta na próxima execução.",
        );
      } else {
        console.error("Erro no sync:", error);
      }
    } finally {
      isSyncing = false;
    }
  };

  onMounted(() => {
    syncPendingUploads();
    window.addEventListener("online", syncPendingUploads);
    syncInterval = setInterval(syncPendingUploads, 30000);
  });

  onUnmounted(() => {
    window.removeEventListener("online", syncPendingUploads);
    if (syncInterval) clearInterval(syncInterval);
  });

  return { syncPendingUploads };
};
